import { useRef, useCallback } from 'react';

interface ThrottleOptions {
  minInterval: number; // Minimum milliseconds between requests
  maxConcurrent?: number; // Max concurrent requests (default 1)
}

/**
 * Client-side request throttling to prevent API spam
 * Ensures minimum time between requests and limits concurrency
 */
export function useRequestThrottle(options: ThrottleOptions) {
  const lastRequestTime = useRef<number>(0);
  const activeRequests = useRef<number>(0);
  const pendingQueue = useRef<(() => void)[]>([]);
  const { minInterval, maxConcurrent = 1 } = options;

  const canMakeRequest = useCallback((): boolean => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime.current;
    const hasCapacity = activeRequests.current < maxConcurrent;
    
    return timeSinceLastRequest >= minInterval && hasCapacity;
  }, [minInterval, maxConcurrent]);

  const getWaitTime = useCallback((): number => {
    const now = Date.now();
    const timeSinceLastRequest = now - lastRequestTime.current;
    const remainingWait = Math.max(0, minInterval - timeSinceLastRequest);
    return remainingWait;
  }, [minInterval]);

  const throttledRequest = useCallback(async <T>(
    requestFn: () => Promise<T>
  ): Promise<T> => {
    // Check if we can make request immediately
    if (!canMakeRequest()) {
      const waitTime = getWaitTime();
      
      if (waitTime > 0) {
        console.log(`⏱️ Throttling: waiting ${waitTime}ms before next request`);
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      
      // Wait for capacity if all slots taken
      if (activeRequests.current >= maxConcurrent) {
        console.log(`⏸️ Throttling: waiting for available slot (${activeRequests.current}/${maxConcurrent} active)`);
        await new Promise<void>((resolve) => {
          pendingQueue.current.push(resolve);
        });
      }
    }

    // Update tracking
    lastRequestTime.current = Date.now();
    activeRequests.current++;

    try {
      const result = await requestFn();
      return result;
    } finally {
      activeRequests.current--;
      
      // Process next queued request
      const next = pendingQueue.current.shift();
      if (next) {
        next();
      }
    }
  }, [canMakeRequest, getWaitTime, maxConcurrent]);

  const resetThrottle = useCallback(() => {
    lastRequestTime.current = 0;
    activeRequests.current = 0;
    pendingQueue.current = [];
  }, []);

  return {
    throttledRequest,
    canMakeRequest,
    getWaitTime,
    resetThrottle,
    activeRequests: activeRequests.current
  };
}
